(function(angular, _) {
    'use strict';

    angular.module('wtApp.dataService')
        .factory('WtAnalysisViewModes', ['$injector', function($injector) {
            const WtObservable = $injector.get('WtObservable');
            const WtPathViewMode = $injector.get('WtPathViewMode');
            const WtComparisonViewMode = $injector.get('WtComparisonViewMode');

            /**
             * @ngdoc service
             * @name WtAnalysisViewModes
             * @description Factory responsible for the analysis view modes management (time series, comparison and other potential view modes)
             */
            return class WtAnalysisViewModes {
                /**
                 * @ngdoc property
                 * @name WtAnalysisViewModes.ACTIONS
                 * @description A map of actions that can be emitted by the viewModes "changes" observable
                 * @returns {Object}
                 * @static
                 */
                static get ACTIONS() {
                    const actions = {
                        /**
                         * Triggered whenever a viewMode is activated/endorsed.
                         */
                        VIEW_MODE_ENDORSED: 'VIEW_MODE_ENDORSED',

                        /**
                         * Triggered whenever a complete viewMode config is initialized.
                         */
                        VIEW_MODE_INIT: 'VIEW_MODE_INIT',

                        /**
                         * Generic event informing about any type of change performed on the view modes configuration
                         */
                        VIEW_MODES_UPDATE: 'VIEW_MODES_UPDATE'
                    };

                    return _.assign(actions, WtPathViewMode.ACTIONS, WtComparisonViewMode.ACTIONS);
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysisViewModes.ACTIVE
                 * @description Returns the property name which defines the currently active view mode.
                 * @returns {string}
                 * @static
                 */
                static get ACTIVE() {
                    return 'active';
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysisViewModes.CONFIG_KEY
                 * @description Returns the property name under which all viewMode configurations reside inside an
                 * analysis config.
                 * @returns {string}
                 * @static
                 */
                static get CONFIG_KEY() {
                    return 'viewMode';
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysisViewModes.STATE_PATHS
                 * @description List of viewMode configuration properties that are related to view state only.
                 * @returns {Array<string>}
                 * @static
                 */
                static get STATE_PATHS() {
                    return _.chain([])
                        .union(WtPathViewMode.STATE_PATHS)
                        .map((viewStatePath) => `${this.CONFIG_KEY}.${viewStatePath}`)
                        .valueOf();
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisViewModes.isActivationPath
                 * @description Determines if a given path of corresponds to the view mode activation path.
                 * @param   {string}  path
                 * @returns {boolean}
                 * @static
                 */
                static isActivationPath(path) {
                    return _.isEqual(path, `${this.CONFIG_KEY}.${this.ACTIVE}`);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisViewModes.isConfigPath
                 * @description Determines if a given path of corresponds to a view mode config path.
                 * @param   {string}  path
                 * @returns {boolean}
                 * @static
                 */
                static isConfigPath(path) {
                    return _.startsWith(path, this.CONFIG_KEY) && !this.isStatePath(path);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisViewModes.isStatePath
                 * @description Determines if a given path of corresponds to a view state path.
                 * @param   {string}  path
                 * @returns {boolean}
                 * @static
                 */
                static isStatePath(path) {
                    return _.includes(this.STATE_PATHS, path);
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysisViewModes#pristineConfig
                 * @description Exposes the pristine viewMode configuration object to the outer world
                 * @returns {Object}
                 */
                get pristineConfig() {
                    return this.pristineData_;
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysisViewModes#config
                 * @description Exposes the viewMode configuration object to the outer world
                 * @returns {Object}
                 */
                get config() {
                    return this.data_;
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysisViewModes#path
                 * @description Exposes the WtPathViewMode instance to the outer world
                 * @returns {Object}
                 */
                get path() {
                    return this.path_;
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysisViewModes#comparison
                 * @description Exposes the WtComparisonViewMode instance to the outer world
                 * @returns {Object}
                 */
                get comparison() {
                    return this.comparison_;
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysisViewModes#actions
                 * @description A map of actions that can be emitted by the viewModes "changes" observable
                 * @returns {Object}
                 */
                get actions() {
                    return WtAnalysisViewModes.ACTIONS;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisViewModes#constructor
                 * @description WtAnalysisViewModes constructor
                 * @param {Object} config The viewMode configuration
                 * @param {Object} configOrigin The origin of the viewMode configuration
                 */
                constructor(config, configOrigin) {
                    /**
                     * Holds the WtComparisonViewMode instance.
                     * @type {null}
                     * @private
                     */
                    this.comparison_ = null;

                    /**
                     * Holds the WtPathViewMode instance.
                     * @type {null}
                     * @private
                     */
                    this.path_ = null;

                    /**
                     * Holds the source the viewMode config originated from
                     * @type {Object}
                     * @private
                     */
                    this.configOrigin_ = _.cloneDeep(configOrigin);

                    /**
                     * Holds the original data
                     * @type {Object}
                     * @private
                     */
                    this.pristineData_ = Object.freeze(_.cloneDeep(config));

                    /**
                     * Holds the current data
                     * @type {Object}
                     * @private
                     */
                    this.data_ = _.cloneDeep(config);

                    /**
                     * A map of callbacks to invoke whenever the viewMode configuration is changed
                     * IMPORTANT: Deregister the callbacks before destroying the observing object to allow it to be garbage collected
                     * TODO Should be replaced in near future by the Observer pattern
                     * @type {Map}
                     * @private
                     */
                    this.onUpdate_ = new Map();

                    /**
                     * The observer object that can be used to push changes into the observable
                     * @example
                     *  - Notify the listeners about a new value: this.changesEmitter_.next('value');
                     *  - Notify the listeners about an error: this.changesEmitter_.error(new Error('Something went wrong'))
                     *  - Notify the listeners about the completion: this.changesEmitter_.complete()
                     * @type {null|Object}
                     * @private
                     */
                    this.changesEmitter_ = null;

                    /**
                     * The observable object that can be subscribed to listen to the changes on the viewModes instance
                     * @example
                     *  const subscription = viewModes.changes.subscribe((action) => {
                     *      const type = action.type;
                     *      const payload = action.payload;
                     *      // React to the viewModes change
                     *  });
                     *
                     *  // Cleanup method should be invoked when the listener is destroyed or does not need the updates
                     *  subscription.unsubscribe();
                     * @type {WtObservable}
                     */
                    this.changes = new WtObservable((observer) => {
                        this.changesEmitter_ = observer;
                    });

                    this.init_();
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisViewModes#init_
                 * @description Initialization phase of the instance
                 * @returns {WtAnalysisViewModes}
                 */
                init_() {
                    if (_.hasIn(this.config, 'comparison')) {
                        const comparisonConfig = _.get(this.config, 'comparison');
                        this.comparison_ = new WtComparisonViewMode(comparisonConfig);

                        this.comparisonSubscription = this.comparison.changes.subscribe(this.onComparisonChange_.bind(this));
                    }

                    if (_.hasIn(this.config, WtPathViewMode.VIEW_MODE_NAME)) {
                        const pathConfig = _.get(this.config, WtPathViewMode.VIEW_MODE_NAME);
                        this.initPath_(pathConfig);
                    }

                    return this;
                }

                initPath_(pathConfig) {
                    const pathConfigOrigin = _.get(this.configOrigin_, WtPathViewMode.VIEW_MODE_NAME);
                    this.path_ = new WtPathViewMode(pathConfig, pathConfigOrigin);
                    this.pathSubscription = this.path.changes.subscribe(this.onPathChange_.bind(this));
                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisViewModes#onPathChange_
                 * @description Handler of path change actions
                 * @returns {WtAnalysisViewModes}
                 * @private
                 */
                onPathChange_({type, payload}) {
                    return this.set(`path.${payload.path}`, payload.currentValue, type);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisViewModes#onComparisonChange_
                 * @description Handler of comparison change actions
                 * @returns {WtAnalysisViewModes}
                 * @private
                 */
                onComparisonChange_({type, payload}) {
                    return this.set(`comparison.${payload.path}`, payload.currentValue, type);
                }

                isComparisonViewMode(analysis) {
                    return analysis.getActiveViewMode() === this.ViewMode.VIEW_MODES.COMPARISON;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisViewModes#getColumns
                 * @description Returns a list of viewMode specific columns.
                 * @param   {string} viewMode
                 * @param   {WtAnalysisColumns} columns
                 * @returns {Array<Object>}
                 * @todo: Implement other view modes too.
                 */
                getColumns(viewMode, columns) {
                    switch (viewMode) {
                        case WtPathViewMode.VIEW_MODE_NAME:
                            return this.path.getColumns(columns);
                        default:
                            return columns.toJSON();
                    }
                }

                //@todo: Change from column list to columns instance.
                canApplyViewMode(viewMode, columns) {
                    switch (viewMode) {
                        case 'comparison':
                            return this.comparison.isActiveComparisonApplicable;
                        case WtPathViewMode.VIEW_MODE_NAME:
                            return this.path.canApplyPath(columns);
                        default:
                            return true;
                    }
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisViewModes#endorsePathViewMode_
                 * @description Initialises/resets the entire config of the path viewMode.
                 * @param   {Object} config
                 * @returns {WtAnalysisViewModes}
                 * @private
                 */
                endorsePathViewMode_(config) {
                    if (this.path === null) {
                        return this.initPath_(config).set(WtPathViewMode.VIEW_MODE_NAME, config, this.actions.VIEW_MODE_INIT);
                    }

                    _.forEach(config, (value, path) => this.path.set(path, value));

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisViewModes#endorseViewMode
                 * @description Initialises/resets the entire config of a viewMode.
                 * @param   {string} viewMode The viewMode to endorse
                 * @param   {Object} config   The viewMode configuration to use
                 * @returns {WtAnalysisViewModes}
                 * @todo: Implement other view modes too.
                 */
                endorseViewMode(viewMode, config) {
                    switch (viewMode) {
                        case WtPathViewMode.VIEW_MODE_NAME:
                            return this.endorsePathViewMode_(config).set(WtAnalysisViewModes.ACTIVE, viewMode, this.actions.VIEW_MODE_ENDORSED);
                        case 'comparison':
                            return this.set(WtAnalysisViewModes.ACTIVE, viewMode, this.actions.VIEW_MODE_ENDORSED);
                        default:
                            return this.set(WtAnalysisViewModes.ACTIVE, viewMode);
                    }
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisViewModes#toJSON
                 * @description Converts the instance into a serializable object
                 * @returns {Object}
                 */
                toJSON() {
                    return this.data_;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisViewModes#toString
                 * @description Stringify the instance
                 * @returns {string}
                 */
                toString() {
                    return JSON.stringify(
                        this.toJSON()
                    );
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisViewModes#dispatchAction
                 * @description Emits an event in the viewModes changes observable
                 * @param {string} type The event name
                 * @param {Object=} payload The payload of the event
                 * @returns {WtAnalysisViewModes}
                 */
                dispatchAction(type, payload = {}) {
                    if (type) {
                        this.changesEmitter_.next({type, payload});
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisViewModes#isPristine
                 * @description Checks if the viewMode configuration was changed since it was initialized
                 * @returns {boolean}
                 */
                isPristine() {
                    return _.isEqual(this.pristineData_, this.data_);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisViewModes#get
                 * @description Analysis viewMode configuration accessor
                 * @param {string} path_ The path within the analysis viewMode configuration (see lodash _.get())
                 * @returns {*}
                 */
                get(path_) {
                    const path = path_ ? String(path_) : null;
                    return path ? _.get(this.data_, path) : null;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisViewModes#set
                 * @description Analysis viewMode configuration setter
                 * @param {string} path_ The path within the analysis viewMode configuration (see lodash _.set())
                 * @param {*} currentValue The value to be set
                 * @param {string} type The type of action to dispatch
                 * @returns {WtAnalysisViewModes}
                 */
                set(path_, currentValue, type = 'VIEW_MODES_UPDATE') {
                    const path = path_ ? String(path_) : null;
                    const previousValue = this.get(path);

                    if (!path || _.isEqual(previousValue, currentValue) || !_.hasIn(this.actions, type)) {
                        return this;
                    }

                    _.set(this.data_, path, currentValue);

                    return this.dispatchAction(type, {path, previousValue, currentValue});
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisViewModes#destroy
                 * @description Method invoked before the instance is destroyed to release the resources and perform the cleanup.
                 */
                destroy() {
                    if (this.comparison_) {
                        this.comparisonSubscription.unsubscribe();
                        this.comparison_.destroy();
                    }

                    if (this.path_) {
                        this.pathSubscription.unsubscribe();
                        this.path_.destroy();
                    }

                    if (this.changesEmitter_) {
                        this.changesEmitter_.complete();
                    }
                }
            };
        }]);
})(window.angular, window._);
